import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import { DOCTOR_SLOT } from '../../../../enums/doctorslot.enum';
import UserService from '../../../../services/User.service';
import type { ReducerAction } from '../../../../types/navigationIndex.types';
import type { Doctor, UserDoctorLink } from '../../../../types/ninox.types';
import type { DoctorSlot } from '../../../../types/user.types';
import { Button, Loading, RadioButton } from '../../../atoms';
import { Calendar } from '../../../organisms/Calendar/Calendar';
import { ProgressStep, ProgressSteps } from '../../../organisms/ProgressSteps/ProgressSteps';
import style from './DateStep.module.scss';

const cx = classNames.bind(style);

type DateStepProps = {
  isFirstAppointment: boolean;
  isOffline: boolean;
  appointmentInfo: any;
  // eslint-disable-next-line no-unused-vars
  setAppointmentInfo: (value: any) => void;
  setCurrentPage: React.Dispatch<ReducerAction>;
};

const stepsOnlineAppointmentClasses = cx({ StepOneOnlineFirstAppointment: true, StepsContainer: true });
const calendarContainerClasses = cx({ CalendarContainer: true });
const buttonConatinerClasses = cx({ ButtonContainer: true });

export function DateStep({
  isFirstAppointment,
  isOffline,
  appointmentInfo,
  setAppointmentInfo,
  setCurrentPage,
}: DateStepProps) {
  const navigate = useNavigate();

  const [doctorSlots, setDoctorSlots] = useState<DoctorSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<DoctorSlot | undefined>();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
  const [selectedDuration, setSelectedDuration] = useState<number>(() => {
    if (isFirstAppointment && typeof appointmentInfo.firstAppointmentDuration === 'number') {
      return [20, 30].includes(appointmentInfo.firstAppointmentDuration)
        ? appointmentInfo.firstAppointmentDuration
        : 20;
    }

    return 20;
  });

  const slotType = React.useMemo(
    () => (isFirstAppointment ? DOCTOR_SLOT.FIRST_APPOINTMENT : DOCTOR_SLOT.FOLLOW_UP_APPOINTMENT),
    [isFirstAppointment]
  );

  useEffect(() => {
    if (
      appointmentInfo.selectedDate !== undefined &&
      new Date(appointmentInfo.selectedDate).getTime() !== selectedDate.getTime()
    ) {
      setSelectedDate(new Date(appointmentInfo.selectedDate));
    }
  }, [appointmentInfo.selectedDate]);

  useEffect(() => {
    if (isFirstAppointment) {
      const incomingDuration = Number(appointmentInfo.firstAppointmentDuration);
      if ([20, 30].includes(incomingDuration) && incomingDuration !== selectedDuration) {
        setSelectedDuration(incomingDuration);
      }
    }
  }, [appointmentInfo.firstAppointmentDuration, isFirstAppointment, selectedDuration]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setDoctorSlots([]);
      setDoctors([]);
      setSelectedSlot(undefined);

      const requestedDuration = isFirstAppointment ? selectedDuration : undefined;
      const slotsResponse = await UserService.fetchConditionalDoctorSlots(
        isFirstAppointment,
        isOffline,
        slotType,
        requestedDuration
      );

      if (slotsResponse.error) {
        if (!slotsResponse.data || slotsResponse.data.length === 0) {
          navigate('/unavailable-appointment');
        }
      } else {
        if (!slotsResponse.data || slotsResponse.data.length === 0) {
          navigate('/unavailable-appointment');
        } else {
          setDoctorSlots(slotsResponse.data);
        }
      }

      //  Fetch and Transform Doctors list for appointmentInfo
      try {
        if (isFirstAppointment) {
          const allDoctorsResponse = await UserService.getAllDoctors();
          if (!allDoctorsResponse.error && allDoctorsResponse.data) {
            setDoctors(allDoctorsResponse.data);
          } else {
            setDoctors([]);
          }
        } else {
          const userLinksResponse = await UserService.getDoctors();
          if (!userLinksResponse.error && userLinksResponse.data) {
            const userLinks: UserDoctorLink[] = userLinksResponse.data;
            const transformedDoctors: Doctor[] = userLinks
              .map((link) => link.doctor)
              .filter((doc): doc is Doctor => !!doc && typeof doc === 'object');
            setDoctors(transformedDoctors);
          } else {
            setDoctors([]);
          }
        }
      } catch (e) {
        setDoctors([]);
      }

      setLoading(false);
    };

    fetchAllData();
  }, [isFirstAppointment, isOffline, navigate, selectedDuration, slotType]);

  useEffect(() => {
    const slotsForSelectedDate = doctorSlots.filter(
      (slot: DoctorSlot) =>
        new Date(new Date(slot.start).setHours(0, 0, 0, 0)).getTime() === new Date(selectedDate).getTime()
    );
    setSelectedSlot(slotsForSelectedDate[0] || undefined);
  }, [selectedDate, doctorSlots]);

  const handleDurationChange = (duration: 20 | 30) => {
    if (duration === selectedDuration) {
      return;
    }

    setSelectedDuration(duration);
    setAppointmentInfo({
      ...appointmentInfo,
      firstAppointmentDuration: duration,
      selectedSlot: undefined,
      doctorSlots: [],
    });
  };

  const next = () => {
    if (selectedSlot && selectedDate) {
      setAppointmentInfo({
        ...appointmentInfo,
        selectedDate: selectedDate,
        doctors: doctors,
        doctorSlots: doctorSlots.filter(
          (slot: DoctorSlot) => new Date(new Date(slot.start).setHours(0, 0, 0, 0)).getTime() === selectedDate.getTime()
        ),
        selectedSlot: selectedSlot,
        firstAppointmentDuration: isFirstAppointment ? selectedDuration : appointmentInfo.firstAppointmentDuration,
      });
      const reducerType = {
        type: '[UPDATE]',
        payload: { appointmentIndex: 2 },
      };
      setCurrentPage(reducerType);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={stepsOnlineAppointmentClasses}>
      <div className='max-w-[550px] mx-auto mb-4'>
        <ProgressSteps>
          {isOffline && isFirstAppointment ? <ProgressStep title='Standort' active /> : null}
          <ProgressStep title='Datum' active />
          <ProgressStep title='Uhrzeit' />
          <ProgressStep title='Information' />
          <ProgressStep title='Bestätigung' />
          <ProgressStep title='Zahlung' />
        </ProgressSteps>
      </div>
      {isFirstAppointment ? (
        <div className=''>
          <div className='text-primary font-semibold mb-3 text-center'>Wie lange soll Ihr Erstgespräch dauern?</div>
          <div className='mb-3 items-center flex gap-4 justify-center'>
            <RadioButton
              id='first-appointment-duration-20'
              name='first-appointment-duration'
              checked={selectedDuration === 20}
              value={20}
              onChange={() => handleDurationChange(20)}
            >
              20 Minuten
            </RadioButton>
            <RadioButton
              id='first-appointment-duration-30'
              name='first-appointment-duration'
              checked={selectedDuration === 30}
              value={30}
              onChange={() => handleDurationChange(30)}
            >
              30 Minuten
            </RadioButton>
          </div>
        </div>
      ) : null}
      <div className='text-primary font-semibold mb-2'>Wann soll Ihr Termin stattfinden?</div>
      {doctorSlots?.length > 0 ? (
        <div className={calendarContainerClasses}>
          <Calendar slots={doctorSlots} onChange={setSelectedDate} selectedDate={selectedDate} />
        </div>
      ) : (
        // Backup if there are no slots available and the navigation to /unavailable-appointment failed
        <div>Keine Termine verfügbar für die gewählten Kriterien.</div>
      )}
      {doctorSlots?.length > 0 && (
        <div className={buttonConatinerClasses}>
          <Button disabled={!selectedDate || !selectedSlot} onClick={next}>
            Weiter
          </Button>
        </div>
      )}
    </div>
  );
}
